import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AppInputField, AppPromptsField } from '@/form/FormUI';
import type { InfoNodeType, NodeFormProps } from '@/nodes';
import { useSavedFormValues } from '@/state';
import { extractFieldErrors } from '@/util/extractFieldErrors';
import { zodResolver } from '@hookform/resolvers/zod';
import { InfoDataSchema, type InfoDataType } from '@rock/types';
import { type FC, useCallback, useEffect } from 'react';
import { type FieldErrors, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';

type FormInput = z.input<typeof InfoDataSchema>;

const InfoForm: FC<NodeFormProps<InfoNodeType>> = ({ flowId, node, updateNode, cancelUpdate }) => {
    const [formState, setFormState] = useSavedFormValues<FormInput>({ flowId, nodeId: node.id });
    const form = useForm<FormInput, unknown, InfoDataType>({
        resolver: zodResolver(InfoDataSchema),
        defaultValues: formState || node.data,
    });

    const watchedFields = useWatch({ control: form.control });
    useEffect(() => {
        setFormState(watchedFields);
    }, [setFormState, watchedFields]);

    const onSubmit = useCallback(
        (values: InfoDataType) => {
            setFormState(null);
            updateNode({
                ...node,
                data: values,
            });
        },
        [setFormState, node, updateNode],
    );

    const onInvalid = useCallback((errors: FieldErrors<FormInput>) => {
        toast.error('Validation Errors', {
            description: extractFieldErrors(errors).join(', '),
        });
    }, []);

    const onCancel = useCallback(() => {
        setFormState(null);
        cancelUpdate();
    }, [setFormState, cancelUpdate]);

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit, onInvalid)}
                className="flex flex-1 flex-col gap-8 items-stretch overflow-hidden"
            >
                <div className="flex flex-1 flex-col gap-8 items-stretch p-5 overflow-scroll">
                    <AppInputField
                        label="Page Title"
                        placeholder="Page Title"
                        control={form.control}
                        name="pageTitle"
                        description="The title displayed in the navigation header."
                    />

                    <AppPromptsField description="What message are we showing the user?" />

                    <AppInputField
                        label="Button Label"
                        placeholder="Continue"
                        control={form.control}
                        name="buttonLabel"
                        description="The label on the button to continue."
                    />
                </div>

                <div className="flex flex-row gap-3 p-5">
                    <Button type="submit" className="flex-1">
                        Save Details
                    </Button>
                    <Button type="button" className="flex-1" variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                </div>
            </form>
        </Form>
    );
};

export default InfoForm;
